import {
  deleteApiV1BoardsIdAssetsAssetId,
  putApiV1BoardsIdAssetsAssetId,
} from '@/open-api/boards/boards';
import type {
  GetBoardAllAssetsItemResponse,
  GetBoardsItemResponse,
} from '@/open-api/model';
import { logEvent } from '@/pages/api/v1/utils';
import { toast } from '@music-vine/cadence/ui';
import debounce from 'lodash/debounce';
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { BOARD_OPERATION_DEBOUNCE_DELAY_MS } from '../context-provider';
import type { AssetBoardOperationProps, BoardAssetId } from '../models';
import type { BoardAssetType } from '@/open-api/model';
import { boardAssetTypeToAssetType } from '../utils/get-board-asset-type-from-asset-group';

interface UseBoardAssetOperationsParams {
  boardAssets: GetBoardAllAssetsItemResponse[];
  boards: GetBoardsItemResponse[];
  favoritesBoardId: number | null;
  setBoardAssets: Dispatch<SetStateAction<GetBoardAllAssetsItemResponse[]>>;
}

export const useBoardAssetOperations = ({
  boards,
  boardAssets,
  setBoardAssets,
  favoritesBoardId,
}: UseBoardAssetOperationsParams) => {
  const assetExistsInBoard = useCallback(
    ({
      assetId,
      assetType,
      boardId,
      boardAssets: boardAssetArg,
    }: AssetBoardOperationProps) => {
      const currentBoardAssets = boardAssetArg || boardAssets;

      const result = currentBoardAssets.find(
        (boardAsset) =>
          boardAsset?.assetId === assetId &&
          boardAsset?.assetType === assetType &&
          boardAsset.boards.flatMap((item) => item?.boardId).includes(boardId)
      );

      return !!result;
    },
    [boardAssets]
  );

  const assetExistsInAnyBoard = useCallback(
    ({
      assetId,
      assetType,
    }: {
      assetId: BoardAssetId;
      assetType: BoardAssetType;
    }) => {
      const result = boardAssets.find(
        (boardAsset) =>
          boardAsset?.assetId === assetId &&
          boardAsset?.assetType === assetType &&
          boardAsset.boards.length > 0
      );

      return !!result;
    },
    [boardAssets]
  );

  const addAssetToBoardContext = useCallback(
    ({ assetId, assetType, boardId }: AssetBoardOperationProps) => {
      setBoardAssets((prev) => {
        const dateCreated = new Date().toISOString();

        const existingAsset = prev.find(
          (item) => item?.assetId === assetId && item?.assetType === assetType
        );

        if (
          existingAsset?.boards
            .flatMap((item) => item?.boardId)
            .includes(boardId)
        ) {
          return prev;
        }

        if (existingAsset) {
          return prev.map((item) => {
            if (item?.assetId === assetId && item?.assetType === assetType) {
              return {
                ...item,
                boards: [...item.boards, { boardId, dateCreated }],
              };
            }
            return item;
          });
        }

        return [
          ...prev,
          { assetId, assetType, boards: [{ boardId, dateCreated }] },
        ];
      });
    },
    []
  );

  const removeAssetFromBoardContext = useCallback(
    ({ assetId, assetType, boardId }: AssetBoardOperationProps) => {
      setBoardAssets((prev) =>
        prev
          .map((boardAsset) => {
            if (
              boardAsset?.assetId === assetId &&
              boardAsset?.assetType === assetType
            ) {
              const newBoards = boardAsset.boards.filter(
                (assetBoard) => assetBoard?.boardId !== boardId
              );

              return {
                ...boardAsset,
                boards: newBoards,
              };
            }
            return boardAsset;
          })
          .filter(
            (boardAsset) => boardAsset?.boards && boardAsset?.boards.length > 0
          )
      );
    },
    []
  );

  const _addAssetToBoard = useCallback(
    async ({ assetId, assetType, boardId }: AssetBoardOperationProps) => {
      addAssetToBoardContext({ assetId, assetType, boardId });

      try {
        await putApiV1BoardsIdAssetsAssetId(boardId, assetId, {
          AssetType: assetType,
        });

        const board = boards.find((b) => b?.id === boardId);
        void logEvent({
          event_type: 'add_to_board',
          asPath: typeof window === 'undefined' ? '' : window.location.pathname,
          metadata: {
            type: 'add',
            asset_type: boardAssetTypeToAssetType(assetType),
            asset_id: assetId,
            board_id: boardId,
            board_name: board?.name,
            is_favorites_board: boardId === favoritesBoardId,
          },
        });
      } catch {
        toast.error("Sorry, we couldn't add asset to board");
        removeAssetFromBoardContext({ assetId, assetType, boardId });
      }
    },
    [
      addAssetToBoardContext,
      removeAssetFromBoardContext,
      boards,
      favoritesBoardId,
    ]
  );

  // Use ref to avoid stale closure in debounced function
  const addAssetToBoardRef = useRef(_addAssetToBoard);
  addAssetToBoardRef.current = _addAssetToBoard;

  const addAssetToBoard = useMemo(
    () =>
      debounce(
        (params: AssetBoardOperationProps) =>
          addAssetToBoardRef.current(params),
        BOARD_OPERATION_DEBOUNCE_DELAY_MS,
        { leading: true }
      ),
    []
  );

  const addAssetToFavoritesBoard = useCallback(
    ({ assetId, assetType }: Omit<AssetBoardOperationProps, 'boardId'>) => {
      const boardId = favoritesBoardId;
      if (!boardId) {
        return;
      }

      addAssetToBoard({ assetId, assetType, boardId });
    },
    [addAssetToBoard, favoritesBoardId]
  );

  const _removeAssetFromBoard = useCallback(
    async ({ assetId, assetType, boardId }: AssetBoardOperationProps) => {
      removeAssetFromBoardContext({ assetId, assetType, boardId });

      try {
        await deleteApiV1BoardsIdAssetsAssetId(boardId, assetId);

        const board = boards.find((b) => b?.id === boardId);
        void logEvent({
          event_type: 'add_to_board',
          asPath: typeof window === 'undefined' ? '' : window.location.pathname,
          metadata: {
            type: 'remove',
            asset_type: boardAssetTypeToAssetType(assetType),
            asset_id: assetId,
            board_id: boardId,
            board_name: board?.name,
            is_favorites_board: boardId === favoritesBoardId,
          },
        });
      } catch {
        toast.error("Sorry, we couldn't remove asset from board");
        addAssetToBoardContext({ assetId, assetType, boardId });
      }
    },
    [
      addAssetToBoardContext,
      removeAssetFromBoardContext,
      boards,
      favoritesBoardId,
    ]
  );

  // Use ref to avoid stale closure in debounced function
  const removeAssetFromBoardRef = useRef(_removeAssetFromBoard);
  removeAssetFromBoardRef.current = _removeAssetFromBoard;

  const removeAssetFromBoard = useMemo(
    () =>
      debounce(
        (params: AssetBoardOperationProps) =>
          removeAssetFromBoardRef.current(params),
        BOARD_OPERATION_DEBOUNCE_DELAY_MS,
        { leading: true }
      ),
    []
  );

  return {
    assetExistsInBoard,
    assetExistsInAnyBoard,
    addAssetToBoard,
    addAssetToFavoritesBoard,
    addAssetToBoardContext,
    removeAssetFromBoard,
    removeAssetFromBoardContext,
  };
};
